import React, { useState, useEffect, useMemo } from "react";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "../../../../style/buttons.scss";
import { BasketItem } from "../../../models/BasketItem";
import { useDispatch, useSelector } from "react-redux";
import { TbShoppingCartPlus } from "react-icons/tb";
import { IoCalendarClearOutline, IoChevronBack, IoLocationOutline } from "react-icons/io5";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import { setAddToBasketMessage } from "../../../store/alerts/alertsActions";
import useFacility from "../../../../utils/hooks/useFacility";
import { useTranslation } from "react-i18next";
import ReactPixel from "react-facebook-pixel";
import { useNavigate } from "react-router";
import { FINALIZE_PAGE } from "../../../routes";
import { State } from "../../../store";
import { QntStateItem } from "../../../models/QntStateItem";
import EAButton from "../../shared/Buttons/EAButton";
import { gaEvent } from "../../../../utils/Analitics/ga";
import { canSendToFacebookPixel, canSendToGA } from "../../../../utils/Analitics/analiticsConfigByFacility";
import { GA_ADD_TO_CART } from "../../../../utils/Analitics/gaEventsConsts";
import EAAlert from "../../shared/EAAlert";
import useBatchAddToBasket from "./hooks/useBatchAddToBasket";
import { errorMessageFromApi } from "../../../utils/apiErrorMessages";
import { format } from "date-fns";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";

export interface PreBasketSummaryContext {
  date?: string;
  time?: string;
  trainer?: string;
  trainerPhoto?: string;
}

export const CalendarSummaryRows: React.FC<{ context?: PreBasketSummaryContext }> = ({ context }) => {
  const { t } = useTranslation();
  if (!context || (!context.date && !context.time && !context.trainer && !context.trainerPhoto)) return null;

  return (
    <>
      {context.date && (
        <div className="pre-basket-summary__row">
          <span>{t("kalendarz_termin")}</span>
          <b>{context.date}</b>
        </div>
      )}
      {context.time && (
        <div className="pre-basket-summary__row">
          <span>{t("godzina_wejscia")}</span>
          <b>{context.time}</b>
        </div>
      )}
      {context.trainer && (
        <div className="pre-basket-summary__row">
          <span>{t("trener_opiekun")}</span>
          <b className="pre-basket-summary__trainer">
            {context.trainerPhoto && (
              <span
                className="pre-basket-summary__avatar"
                style={{ backgroundImage: `url(${context.trainerPhoto})` }}
                aria-hidden={true}
              />
            )}
            {context.trainer}
          </b>
        </div>
      )}
      <hr className="pre-basket-summary__divider" />
    </>
  );
};

interface IProps {
  preBasket: BasketItem[];
  clearBasket: () => void;
  ticketLimit?: number;
  qntState?: QntStateItem[];
  onBack?: () => void;
  /** "calendar" – sprzedaż kalendarzowa. "lines" – linie terminu i stopka jak w modalu karnetu. */
  variant?: "default" | "calendar" | "lines";
  /** Wybrany termin pokazywany nad sumą (tylko variant="calendar"). */
  context?: PreBasketSummaryContext;
  /** Data i godzina wydarzenia wyświetlana po lewej stronie podsumowania. */
  eventDate?: number | Date;
  /** Godzina zakończenia, dopisywana do terminu jako zakres. */
  eventEndDate?: number | Date;
  /** Miejsce wydarzenia pod terminem. */
  eventLocation?: string;
  /** Wywoływane po udanym dodaniu zamiast globalnego komunikatu (chyba że obiekt przekierowuje od razu do koszyka). */
  onAdded?: () => void;
}

const formatSummaryPrice = (val: number): string => {
  return Number.isInteger(val) ? val.toString() : val.toFixed(2);
};

const PreBasketSummary: React.FC<IProps> = ({
  preBasket,
  clearBasket,
  ticketLimit,
  qntState,
  onBack,
  variant = "default",
  context,
  eventDate,
  eventEndDate,
  eventLocation,
  onAdded,
}: IProps) => {
  const { addToBasket } = useBatchAddToBasket();
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useDesktopSize();
  const facilityIds = useFacility();
  const [alert, setAlert] = useState<string>("");
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);
  const saleConfiguration = useSelector((state: State) => state.config.configuration);

  const formattedEventDate = useMemo(() => {
    const rawDate = eventDate ?? preBasket[0]?.ticketAccess?.[0]?.startDate;
    if (!rawDate) return null;
    const dateObj = rawDate instanceof Date ? rawDate : new Date(rawDate);
    if (isNaN(dateObj.getTime())) return null;

    const datePart = format(dateObj, "EEEE, d MMMM yyyy", { locale });
    let timePart = format(dateObj, "HH:mm");

    if (eventEndDate) {
      const endObj = eventEndDate instanceof Date ? eventEndDate : new Date(eventEndDate);
      if (!isNaN(endObj.getTime()) && endObj.getTime() > dateObj.getTime()) {
        timePart = `${timePart}–${format(endObj, "HH:mm")}`;
      }
    }

    return `${datePart} • ${timePart}`;
  }, [eventDate, eventEndDate, preBasket, locale]);

  const locationLabel = eventLocation?.trim() ?? "";
  const hasLocation = locationLabel.length > 1;

  // Timer dla alertu - ukrywa alert po 5 sekundach
  useEffect(() => {
    if (alert && alert.length > 2 && alert !== t("dodano_bilet_do_koszyka")) {
      const timer = setTimeout(() => {
        setAlert("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alert, t]);

  const saveBasket = async () => {
    setAlert("");
    const result = await addToBasket(preBasket);
    if (result.isError) {
      setAlert(errorMessageFromApi(result));
      return;
    }
    clearBasket();
    if (onAdded && saleConfiguration.popupAfterAddTicket !== false) {
      onAdded();
      return;
    }
    finalizeFunction();
  };

  const finalizeFunction = () => {
    if (saleConfiguration.popupAfterAddTicket === true) {
      dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
      //  scrollToId('AddToBasketMessage')
    }

    if (saleConfiguration.popupAfterAddTicket === false) {
      setTimeout(() => {
        navigate(FINALIZE_PAGE);
      }, 300);
    }
  };

  const pixelAddBasket = () => {
    // Calculate basket total
    const basketTotal = preBasket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);

    // Google Analytics - Fix for proper GA4 ecommerce tracking
    if (canSendToGA(facilityIds)) {
      gaEvent(
        facilityIds,
        "ecommerce",
        GA_ADD_TO_CART,
        {
          event_category: "ecommerce",
          currency: "PLN",
          value: basketTotal,
          items: preBasket.map((item) => ({
            item_id: item.assortIds.replace(/^0,\+/, ""),
            item_name: item.assortName || "Produkt",
            price: item.price,
            quantity: item.qnt,
          })),
        },
        "add_to_cart",
      );
    }

    // Facebook Pixel
    if (canSendToFacebookPixel(facilityIds)) {
      const pixelValue = preBasket.length > 0 ? preBasket[0].price * preBasket[0].qnt : 0;
      ReactPixel.fbq("track", "addEventTicketToBasket", {
        value: pixelValue,
        quantity: preBasket.length > 0 ? preBasket[0].qnt : 0,
        currency: "PLN",
      });
    }
  };
  const sumBasket = () => preBasket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);
  const isEmpty = preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0) <= 0;

  useEffect(() => {
    if (!isEmpty) setShowEmptyWarning(false);
  }, [isEmpty]);

  const handleAddClick = () => {
    if (isEmpty) {
      setShowEmptyWarning(true);
      return;
    }
    setShowEmptyWarning(false);
    saveBasket();
    pixelAddBasket();
  };

  if (variant === "lines") {
    return (
      <div className="pre-basket-summary pre-basket-summary--lines">
        {alert && <EAAlert type="error" message={alert} />}

        <div className="pre-basket-summary__lines-card">
          {(formattedEventDate || hasLocation) && (
            <div className="pre-basket-summary__lines">
              {formattedEventDate && (
                <div className="pre-basket-summary__line">
                  <span>{t("termin")}</span>
                  <b>{formattedEventDate}</b>
                </div>
              )}
              {hasLocation && (
                <div className="pre-basket-summary__line">
                  <span>{t("strefa")}</span>
                  <b>{locationLabel}</b>
                </div>
              )}
            </div>
          )}

          <div className="pre-basket-summary__lines-row">
            <div className="pre-basket-summary__lines-price">
              <span>{t("razem")}</span>
              <b>
                {formatSummaryPrice(sumBasket())} {t("zl")}
              </b>
            </div>
            <EAButton
              title={isDesktop ? t("dodaj_do_koszyka") : t("dodaj")}
              endIcon={<TbShoppingCartPlus />}
              onClick={handleAddClick}
              animationType="addToBasket"
            />
          </div>
        </div>

        {showEmptyWarning && (
          <div className="pre-basket-summary__validation">
            <span>Dodaj co najmniej 1 bilet</span>
          </div>
        )}
      </div>
    );
  }

  if (variant === "calendar") {
    return (
      <div className="pre-basket-summary__container pre-basket-summary--calendar">
        <CalendarSummaryRows context={context} />

        <div className="pre-basket-summary__total">
          <span>{t("razem")}</span>
          <b>
            {sumBasket().toFixed(2)} {t("zl")}
          </b>
        </div>

        <EAButton
          title={t("dodaj_do_koszyka")}
          endIcon={<TbShoppingCartPlus />}
          onClick={handleAddClick}
          animationType="addToBasket"
          fullWidth
        />
        {showEmptyWarning && <EAAlert type="warning" message={t("kalendarz_dodaj_co_najmniej_jeden_bilet")} />}
        {alert && <EAAlert type="error" message={alert} />}
      </div>
    );
  }

  return (
    <div className="pre-basket-summary">
      {alert && <EAAlert type="error" message={alert} />}

      <div className="pre-basket-summary__container">
        <div className="pre-basket-summary__left">
          {formattedEventDate && (
            <div className="pre-basket-summary__event-date">
              <IoCalendarClearOutline className="pre-basket-summary__event-date-icon" aria-hidden={true} />
              <span>{formattedEventDate}</span>
            </div>
          )}
          {hasLocation && (
            <div className="pre-basket-summary__event-location">
              <IoLocationOutline className="pre-basket-summary__event-date-icon" aria-hidden={true} />
              <span>{locationLabel}</span>
            </div>
          )}
        </div>

        <div className="pre-basket-summary__right">
          <div className="pre-basket-summary__total-box">
            <span className="pre-basket-summary__total-label">{t("razem")}</span>
            <b className="pre-basket-summary__total-value">
              {formatSummaryPrice(sumBasket())} {t("zl")}
            </b>
          </div>

          <div className="pre-basket-summary__buttons">
            <EAButton
              title={isDesktop ? t("dodaj_do_koszyka") : t("dodaj")}
              endIcon={<TbShoppingCartPlus />}
              onClick={handleAddClick}
              animationType="addToBasket"
            />
          </div>
        </div>
      </div>

      {showEmptyWarning && (
        <div className="pre-basket-summary__validation">
          <span>Dodaj co najmniej 1 bilet</span>
        </div>
      )}

      {onBack && !isDesktop && (
        <EAButton title={t("Powrót")} variant="text" startIcon={<IoChevronBack />} onClick={onBack} fullWidth />
      )}
    </div>
  );
};

export default PreBasketSummary;
